import { ChangeDetectionStrategy, Component, NgZone, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { IonicModule, Platform } from '@ionic/angular';
import { DswToastService } from '@vi-succ/design-system-web';
import { Catalog } from '@vi-succ/products-domain';
import { ShoppingCartFacade } from '@vi-succ/shoppingcart-domain';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { NGXLogger } from 'ngx-logger';
import { filter, first, forkJoin, take } from 'rxjs';
import { ProtectedTranslateService } from './core/locale/services/protected-translate.service';
import { AuthService } from './core/login/services/auth.service';
import { FirebaseService } from './core/tracking/services/firebase.service';
import { EtCatalogDataService } from './services/et-catalog-data.service';
import { EtShoppingCartDataService } from './services/et-shopping-cart-data.service';
import { InstanaService } from './services/instana.service';
import { NgxTranslatePaginatorIntl } from './services/ngx-translate-paginator-intl';
import { StorageService } from './services/storage.service';
import { TrackingService } from './services/tracking.service';
import { SetupLoginComponent } from './shared/setup-login/setup-login.component';
import { createDeepMutableClone } from './util/object.util';

declare global {
  interface Window {
    dataLayer: any;
  }
}

@Component({
  selector: 'et-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule],
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: NgxTranslatePaginatorIntl,
    },
  ],
})
export class AppComponent implements OnInit {
  constructor(
    private logger: NGXLogger,
    private platform: Platform,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private zone: NgZone,
    private renderer: Renderer2,
    private authService: AuthService,
    private toastService: DswToastService,
    private translate: ProtectedTranslateService,
    private storageService: StorageService,
    private catalogService: EtCatalogDataService,
    private instanaService: InstanaService,
    private trackingService: TrackingService,
    private shoppingCartFacade: ShoppingCartFacade,
    private shoppingCartService: EtShoppingCartDataService,
    private swUpdate: SwUpdate,
    private analyticsService: FirebaseService,
  ) {}

  async ngOnInit() {
    this.handleUpdates();
    this.listenForAnalyticsConsent();
    this.setUserSelectForMobile();

    this.loadCatalogs();

    await this.importGoogleMaps();
    await this.authService.init();
    await SplashScreen.hide();

    const persistedValue = await this.storageService.get('setupWizardRan');
    const setupWizardRan = (persistedValue ?? 'false') === 'true';
    const isLoggedIn = this.authService.hasValidCsrfToken();
    const noSetupQueryParam = this.route.snapshot.queryParamMap.get('noSetup') === 'true';
    const catalogQueryParam = this.route.snapshot.queryParamMap.get('salesOrg');
    const languageQueryParam = this.route.snapshot.queryParamMap.get('language');

    if (!setupWizardRan && !isLoggedIn && !noSetupQueryParam) {
      this.dialog
        .open<SetupLoginComponent, void, boolean>(SetupLoginComponent, {
          width: '100%',
          maxWidth: '100vw',
          height: '100%',
          maxHeight: '100vh',
          panelClass: 'setup-login-dialog-container',
        })
        .afterClosed()
        .subscribe(async (success) => {
          if (success) {
            await this.storageService.set('setupWizardRan', 'true');
            await this.router.navigate(['/']);
          }
        });
    }

    if (noSetupQueryParam) {
      if (!!catalogQueryParam && !!languageQueryParam) {
        this.catalogService
          .getCatalogs()
          .pipe(take(1))
          .subscribe((catalogs) => {
            const catalog = catalogs.find(
              (c) =>
                c.salesOrg.toLowerCase() === catalogQueryParam.toLowerCase() &&
                c.language.toLowerCase() === languageQueryParam.toLowerCase(),
            );

            if (catalog) {
              this.catalogService.setActiveCatalog(catalog).then(() => {
                this.updateShoppingCartCatalog(catalog);
              });
            }
          });
      }
      await this.storageService.set('setupWizardRan', 'true');
    }

    this.addAppUrlListener();
    this.configureBackButton();
    await this.setUpTracking();
  }

  private loadCatalogs() {
    forkJoin([
      this.catalogService.getCatalogs().pipe(first()),
      this.catalogService.getCurrentCatalog().pipe(first()),
    ]).subscribe(([catalogs, catalog]) => {
      this.catalogService.setAvailableCatalogs(catalogs);
      this.catalogService.setActiveCatalog(catalog).then(() => {
        this.updateShoppingCartCatalog(catalog);
      });
    });
  }

  private updateShoppingCartCatalog(catalog: Catalog) {
    this.shoppingCartService
      .getShoppingCart()
      .pipe(
        filter((cart) => !!cart),
        take(1),
      )
      .subscribe((cart) => {
        if (cart.salesOrg !== catalog.salesOrg) {
          const updatedCart = createDeepMutableClone(cart);
          updatedCart.salesOrg = catalog.salesOrg;
          this.shoppingCartFacade.updateShoppingCartContent(updatedCart);
        }
      });
  }

  private async importGoogleMaps() {
    try {
      await google.maps.importLibrary('core');
      await google.maps.importLibrary('maps');
    } catch (e) {
      this.logger.error('Error importing google maps libraries');
    }
  }

  private addAppUrlListener() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const slug = event.url.split('capacitor/com.viessmann.viparts').pop();
        if (slug === '/cart/complete') {
          this.shoppingCartFacade.resetCart();
          this.router.navigateByUrl('/overview');
          const toast = this.translate.instant('shopping_cart.order.complete');
          this.toastService.open(toast, 'success');
        } else if (slug === '/cart?checkout=true') {
          this.router.navigateByUrl('/cart?checkout=true');
        }
      });
    });
  }

  private async setUpTracking() {
    this.instanaService.startSessionTracking();

    let enableTracking = true; // default for non-iOS

    if (this.platform.is('ios')) {
      const status = await AppTrackingTransparency.getStatus();

      if (status.status === 'notDetermined' || status.status === 'restricted') {
        const permission = await AppTrackingTransparency.requestPermission();
        enableTracking = permission.status === 'authorized';
      } else if (status.status === 'denied') {
        enableTracking = false;
      }
    }

    this.trackingService.setTrackingConsent(enableTracking ? 'true' : 'false');
  }

  private configureBackButton() {
    App.addListener('backButton', (event) => {
      if (!event.canGoBack) {
        App.exitApp();
      } else {
        window.history.back();
      }
    });
  }

  private setUserSelectForMobile(): void {
    const desktop = this.platform.is('desktop');

    if (!desktop) {
      this.renderer.addClass(document.body, 'select-none');
    }
  }

  private handleUpdates() {
    if (this.swUpdate.isEnabled) {
      // Subscribe to update notifications
      this.swUpdate.versionUpdates.subscribe((event) => {
        console.log('A new version is available!', event);

        // Force update
        this.swUpdate.activateUpdate().then(() => {
          console.log('New version activated, reloading page...');
          document.location.reload();
        });
      });
    }
  }

  private listenForAnalyticsConsent() {
    window.dataLayer = window.dataLayer || [];

    const originalPush = window.dataLayer.push;
    window.dataLayer.push = (...args: any[]) => {
      args.forEach((entry: any) => {
        // case 1: GTM consent update array-style
        if (Array.isArray(entry) || (entry[0] === 'consent' && entry[1] === 'update')) {
          const consent = entry[2] || {};
          if (consent.analytics_storage === 'granted') {
            this.analyticsService.setEnableAnalytics(true);
          }
          if (consent.analytics_storage === 'denied') {
            this.analyticsService.setEnableAnalytics(false);
          }
        }

        // case 2: Usercentrics "consent_status" object event
        if (entry.event === 'consent_status') {
          const analyticsAllowed = entry.ucCategory?.marketing || entry['Google Analytics 4'];
          if (analyticsAllowed) {
            this.analyticsService.setEnableAnalytics(true);
          } else {
            this.analyticsService.setEnableAnalytics(false);
          }
        }
      });

      return originalPush.apply(window.dataLayer, args);
    };
  }
}
